import { ArrowLeft, KeyRound, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Brand } from "../../components/Brand";
import { api, getApiErrorMessage } from "../../lib/api";
import { authContinuationCopy, authContinuationFromPath } from "../../lib/authContinuation";
import { talkBrand } from "../../lib/brand";
import { safeReturnPath } from "../../lib/navigation";
import { ui } from "../../lib/ui";
import { useAuth } from "./AuthContext";

export function LoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestedPath = safeReturnPath((location.state as { from?: unknown } | null)?.from);
  const continuation = authContinuationFromPath(requestedPath);
  const copy = authContinuationCopy(continuation);

  if (!auth.loading && auth.snapshot) return <Navigate replace to={requestedPath} />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!identifier.trim() || !password || busy) return;
    setBusy(true);
    setError("");
    try {
      const snapshot = await api.auth.login({ identifier: identifier.trim(), password });
      if (!snapshot?.user?.id || !snapshot.csrfToken) throw new Error(ui("The server returned an incomplete account session.", "Сервер вернул неполную сессию. Попробуйте ещё раз."));
      auth.setSnapshot(snapshot);
      navigate(requestedPath, { replace: true });
    } catch (nextError) {
      setError(getApiErrorMessage(nextError));
      setBusy(false);
    }
  }

  return (
    <main id="main-content" className="auth-layout" tabIndex={-1}>
      <section className="auth-context">
        <Brand />
        <div>
          <h1>{copy.contextTitle}</h1>
          <p>{copy.contextBody}</p>
        </div>
        <Link className="quiet-link" to="/"><ArrowLeft aria-hidden="true" size={16} />{ui("Back home", "На главную")}</Link>
      </section>
      <section className="auth-surface" aria-labelledby="login-title">
        <div className="auth-surface__heading"><KeyRound aria-hidden="true" size={24} /><div><h2 id="login-title">{copy.formTitle}</h2><p>{copy.formBody}</p></div></div>
        <form className="form-stack" onSubmit={(event) => void submit(event)}>
          <label className="field"><span>{ui("Handle", "Имя аккаунта")}</span><input autoFocus autoComplete="username" required value={identifier} onChange={(event) => setIdentifier(event.target.value)} disabled={busy} /></label>
          <label className="field"><span>{ui("Password", "Пароль")}</span><input type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} /></label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="button button--primary button--wide" type="submit" disabled={busy || !identifier.trim() || !password}>
            {busy ? <LoaderCircle className="spin" aria-hidden="true" size={18} /> : null}{busy ? ui("Signing in…", "Входим…") : continuation ? ui("Sign in and continue", "Войти и продолжить") : ui("Sign in", "Войти")}
          </button>
        </form>
        <div className="auth-footnote"><span>{ui(`New to ${talkBrand.organizationName}?`, "Ещё нет аккаунта?")}</span><Link to="/register" state={{ from: requestedPath }}>{ui("Create an account", "Создать аккаунт")}</Link></div>
      </section>
    </main>
  );
}
